import { Form } from 'antd';
import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { Button } from 'shared/ui/Button';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ReactComponent as EditIcon } from 'shared/assets/icons/EditIcon.svg';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useGetCurrencySymbol } from 'app/appState';
import { RentalItemModal } from './RentalItemModal';
import { createContractModalActions } from 'widgets/CreateContractModal/model/slice/createContractModalSlice';
import { RentalItem } from 'entities/RentalItem';

export const RentalItemsForm: FC = memo(() => {
  const { t } = useAppTranslation(['contracts', 'booking', 'common']);

  const currencySymbol = useGetCurrencySymbol();
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();

  const [isOpened, setOpened] = useState(false);
  const [editingItem, setEditingItem] = useState<Nullable<RentalItem>>(null);
  const [editingIndex, setEditingIndex] = useState<Nullable<number>>(null);

  const openAddModal = useCallback(() => {
    setEditingItem(null);
    setEditingIndex(null);
    setOpened(true);
  }, []);

  const openEditModal = useCallback((item: RentalItem, index: number) => {
    setEditingItem(item);
    setEditingIndex(index);
    setOpened(true);
  }, []);

  const closeModal = useCallback(() => {
    setOpened(false);
  }, []);

  const handleItemModalSuccess = useCallback(
    (values: RentalItem, add: (value: RentalItem) => void) => {
      const items = form.getFieldValue('rentalItems') || [];
      const newItems = Array.isArray(items) ? [...items] : [];

      if (editingIndex !== null && typeof editingIndex === 'number') {
        newItems[editingIndex] = values;
        form.setFieldsValue({ rentalItems: newItems });
        dispatch(createContractModalActions.setRentalItems(newItems));
      } else {
        add(values);
      }
    },
    [form, editingIndex, dispatch],
  );

  useEffect(() => {
    dispatch(createContractModalActions.setRentalItems([]));
  }, [dispatch]);

  return (
    <Form
      className="flex flex-col"
      form={form}
      layout="vertical"
      onValuesChange={(_, allValues) => {
        dispatch(createContractModalActions.setRentalItems(allValues.rentalItems || []));
      }}
    >
      <div className="flex justify-between mb-2">
        <div className="font-semibold">{t('Rental items')}</div>
        <Button
          icon={<PlusIcon className="[&>path]:stroke-white" />}
          iconPosition="prev"
          size="small"
          fontSize="small"
          containerClassName="w-fit"
          className="!py-1.5"
          onClick={openAddModal}
        >
          {t('Add item')}
        </Button>
      </div>

      <div className="text-sm text-primaryLight mb-3">
        <sup>*</sup>
        {t('Added rental items, including their specified discount and tax, will be included in all future invoices of the rental period')}
      </div>

      <div className="flex gap-10">
        <div className="flex-1">
          <Form.List name="rentalItems">
            {(fields, { add, remove }) => {
              return (
                <>
                  {fields.map(({ key, name }) => {
                    const item = form.getFieldValue(['rentalItems', name]);

                    return (
                      <div key={key} className="border border-border rounded-md p-4 flex justify-between items-center mb-3">
                        <div>
                          <div className="flex gap-2 font-medium">
                            <div>{t(item?.rentalItemType, { ns: 'common' })}</div>
                            <div>
                              {item?.quantity} × {roundNumber(item?.totalPrice / item?.quantity)} {currencySymbol}
                            </div>
                          </div>
                          <div className="text-primaryLight">{item?.description}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            theme="secondary"
                            size="small"
                            className="!p-[5px]"
                            icon={<EditIcon />}
                            onClick={() => {
                              openEditModal(item, name);
                            }}
                          />
                          <Button
                            size="small"
                            theme="danger"
                            fontSize="small"
                            className="!px-[10px] !py-[6px]"
                            onClick={() => {
                              remove(name);
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  <RentalItemModal
                    isOpened={isOpened}
                    onClose={closeModal}
                    rentalItem={editingItem}
                    onSuccess={(values) => {
                      handleItemModalSuccess(values, add);
                    }}
                  />
                </>
              );
            }}
          </Form.List>
        </div>
      </div>
    </Form>
  );
});
